import React from "react";
import moment from "moment";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logo from "../../icons/latest-logo.png";
import "./assessmentFormPrint.css";

const TRAINING_GOALS = [
  "Fat Loss",
  "Muscle Gain",
  "Strength",
  "Rehab",
  "General Fitness",
];

export const MEDICAL_ROWS = [
  { key: "med_blood_pressure", label: "Blood Pressure (Low/High)" },
  { key: "med_heart_disease", label: "Heart Disease / Issues" },
  { key: "med_diabetes", label: "Diabetes (Self or Family)" },
  { key: "med_high_cholesterol", label: "High Cholesterol" },
  { key: "med_breathing_asthma", label: "Breathing Difficulties / Asthma" },
  { key: "med_leg_cramps", label: "Frequent Leg Cramps" },
  { key: "med_swollen_ankles", label: "Swollen Ankles" },
  { key: "med_spinal_joint_pain", label: "Spinal / Joint Pain" },
  { key: "med_recent_surgery", label: "Recent Surgery" },
  { key: "med_pregnancy_postpartum", label: "Pregnancy/Postpartum" },
  { key: "med_c_section", label: "C-Section Delivery (Females)" },
];

export const POSTURE_ROWS = [
  { key: "posture_head_neck", label: "Head & Neck Alignment" },
  { key: "posture_shoulder_level", label: "Shoulder Level" },
  { key: "posture_pelvic_alignment", label: "Pelvic Alignment" },
  { key: "posture_knee_position", label: "Knee Position" },
  { key: "posture_foot_ankle", label: "Foot/Ankle Alignment" },
  { key: "posture_gait_pattern", label: "Gait Pattern" },
  { key: "posture_balance_coordination", label: "Balance & Coordination" },
];

export const LIFESTYLE_ROWS = [
  { key: "occupation", label: "Occupation", hint: "" },
  {
    key: "activity_level",
    label: "Activity Level",
    hint: "Sedentary / Moderate / Active",
  },
  { key: "sleep_hours", label: "Sleep Hours", hint: "" },
  { key: "water_intake", label: "Water Intake", hint: "" },
  {
    key: "diet_type",
    label: "Diet Type",
    hint: "Balanced / High Carb / High Fat / Poor",
  },
  {
    key: "smoking_substance",
    label: "Smoking / Any other Substance",
    hint: "YES/NO",
  },
  { key: "stress_level", label: "Stress Level", hint: "Low / Moderate / High" },
];

const has = (v) => v !== null && v !== undefined && String(v).trim() !== "";
const show = (v) => (has(v) ? String(v).trim() : "");

const Field = ({ label, value }) => (
  <div className="caf-field">
    <span className="caf-field-label">{label}</span>
    <span className="caf-field-value">{show(value)}</span>
  </div>
);

const ValueRow = ({ label, value, hint }) => (
  <tr>
    <td>{label}</td>
    <td className="caf-value">
      {has(value) ? (
        show(value)
      ) : hint ? (
        <span className="caf-hint">{hint}</span>
      ) : (
        ""
      )}
    </td>
  </tr>
);

const SectionHead = ({ title, note }) => (
  <div className="caf-section-head">
    <h3 className="caf-section-title">{title}</h3>
    {note ? <span className="caf-section-note">{note}</span> : null}
  </div>
);

const PageHeader = () => (
  <div className="caf-head">
    <img src={logo} alt="Vostro World" className="caf-logo" crossOrigin="anonymous" />
    <div className="caf-head-text">
      <h2 className="caf-title">Client Assessment Form</h2>
      <p className="caf-subtitle">Fitness &amp; Performance Evaluation</p>
    </div>
  </div>
);

const PageFooter = ({ page }) => (
  <div className="caf-footer">
    <span>Vostro World • Client Assessment Form</span>
    <span>Page {page} of 2</span>
  </div>
);

/**
 * Two-page A4 rendering of the paper assessment form.
 * `data` uses the same keys as the post_assessments record.
 */
export default function AssessmentFormPrint({ data = {} }) {
  const fatLine = [
    has(data.fat) ? `Body FAT ${show(data.fat)}%` : "",
    has(data.v_fat) ? `V-FAT ${show(data.v_fat)}` : "",
  ]
    .filter(Boolean)
    .join("  |  ");

  const goal = show(data.training_goal).toLowerCase();

  return (
    <div className="caf-print-root">
      {/* ─────────────────────────── Page 1 ─────────────────────────── */}
      <div className="caf-page">
        <PageHeader />

        <div className="caf-meta">
          <Field
            label="Assessment Date:"
            value={
              has(data.date) ? moment(data.date).format("DD MMM YYYY") : ""
            }
          />
          <Field label="Trainer Name:" value={data.trainer_name} />
        </div>

        <SectionHead title="Section 1 - Client Information" />
        <div className="caf-client-top">
          <Field label="Name:" value={data.client_name} />
          <Field label="Age:" value={data.age} />
          <Field label="Gender:" value={data.gender} />
        </div>
        <div className="caf-client-bottom">
          <Field label="Contact:" value={data.contact_no} />
          <Field
            label="Emergency Contact No:"
            value={data.emergency_contact_no}
          />
        </div>
        <p className="caf-goal-line caf-goal-options">
          <strong>Training Goal: </strong>
          {TRAINING_GOALS.map((g, i) => (
            <span
              key={g}
              className={goal === g.toLowerCase() ? "caf-goal-selected" : ""}
            >
              {g}
              {i < TRAINING_GOALS.length - 1 ? " / " : " / "}
            </span>
          ))}
          <span className={goal === "other" ? "caf-goal-selected" : ""}>
            Other:
          </span>{" "}
          {show(data.training_goal_other)}
        </p>

        <SectionHead
          title="Section 2 - Medical & Health Screening"
          note='Write "YES" if applicable'
        />
        <table className="caf-table">
          <thead>
            <tr>
              <th>Medical Condition</th>
              <th className="caf-col-yesno">Yes/No</th>
            </tr>
          </thead>
          <tbody>
            {MEDICAL_ROWS.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td className="caf-yesno">
                  {show(data[row.key]).toUpperCase()}
                </td>
              </tr>
            ))}
            <tr>
              <td>Any Other Condition</td>
              <td className="caf-yesno">{show(data.med_other_condition)}</td>
            </tr>
          </tbody>
        </table>

        <SectionHead title="Section 3 - Body Stats" />
        <table className="caf-table">
          <thead>
            <tr>
              <th>Measurement</th>
              <th className="caf-col-result">Result</th>
            </tr>
          </thead>
          <tbody>
            <ValueRow label="Height (cm)" value={data.height} />
            <ValueRow label="Weight" value={data.weight} />
            <ValueRow label="Fat %age: Body FAT/V-FAT" value={fatLine} />
            <ValueRow label="BMI" value={data.body_mass_index} />
          </tbody>
        </table>

        <SectionHead title="Section 4 - Measurements (cm)" />
        <table className="caf-table">
          <thead>
            <tr>
              <th>Body Part</th>
              <th className="caf-col-result">Measurement</th>
            </tr>
          </thead>
          <tbody>
            <ValueRow label="Chest" value={data.chest} />
            <ValueRow
              label="Upper Belly (5 cm above navel)"
              value={data.upper_belly}
            />
            <ValueRow
              label="Mid Belly (At navel level)"
              value={has(data.mid_belly) ? data.mid_belly : data.waist}
            />
            <ValueRow
              label="Lower Belly (5 cm below navel)"
              value={data.lower_belly}
            />
            <ValueRow
              label="Hips"
              value={has(data.hips) ? data.hips : data.gluts}
            />
            <ValueRow label="Thighs" value={data.thigh} />
            <ValueRow label="Arms" value={data.arm} />
          </tbody>
        </table>

        <PageFooter page={1} />
      </div>

      {/* ─────────────────────────── Page 2 ─────────────────────────── */}
      <div className="caf-page">
        <PageHeader />

        <SectionHead title="Section 5 - Lifestyle & Habits" />
        <table className="caf-table">
          <thead>
            <tr>
              <th>Question</th>
              <th className="caf-col-result">Response</th>
            </tr>
          </thead>
          <tbody>
            {LIFESTYLE_ROWS.map((row) => (
              <ValueRow
                key={row.key}
                label={row.label}
                value={data[row.key]}
                hint={row.hint}
              />
            ))}
          </tbody>
        </table>

        <SectionHead title="Section 6 - Postural Assessment (Optional)" />
        <table className="caf-table">
          <thead>
            <tr>
              <th>Postural Assessment Area</th>
              <th className="caf-col-result">Observations</th>
            </tr>
          </thead>
          <tbody>
            {POSTURE_ROWS.map((row) => (
              <ValueRow key={row.key} label={row.label} value={data[row.key]} />
            ))}
          </tbody>
        </table>
        <div className="caf-recommendation">
          <span className="caf-recommendation-label">
            Clinical Recommendation:
          </span>
          {show(data.clinical_recommendation)}
        </div>

        <SectionHead title="Section 7 - Training Objectives" />
        <div className="caf-objectives">
          <Field label="Primary Goal:" value={data.primary_goal} />
          <Field label="Secondary Goal:" value={data.secondary_goal} />
        </div>

        <SectionHead title="Section 8 - Trainer Final Notes" />
        <div className="caf-notes-box">{show(data.trainer_notes)}</div>

        <PageFooter page={2} />
      </div>
    </div>
  );
}

/**
 * Renders the hidden print layout to a 2-page A4 PDF and saves it.
 */
export async function downloadAssessmentFormPdf(printRootEl, fileName) {
  if (!printRootEl) throw new Error("Print element not found");

  const pages = printRootEl.querySelectorAll(".caf-page");
  if (!pages.length) throw new Error("No print pages found");

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  for (let i = 0; i < pages.length; i++) {
    const canvas = await html2canvas(pages[i], {
      scale: 2,
      backgroundColor: "#ffffff",
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: pages[i].scrollWidth,
      height: pages[i].scrollHeight,
      windowWidth: pages[i].scrollWidth,
      windowHeight: pages[i].scrollHeight,
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.9);
    const imgH = (canvas.height * pageWidth) / canvas.width;

    if (i > 0) pdf.addPage("a4", "portrait");

    if (imgH <= pageHeight) {
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, imgH);
    } else {
      const fitW = (canvas.width * pageHeight) / canvas.height;
      pdf.addImage(imgData, "JPEG", (pageWidth - fitW) / 2, 0, fitW, pageHeight);
    }
  }

  const safeName = (fileName || "client").replace(/[^\w-]+/g, "_");
  pdf.save(`Client_Assessment_Form_${safeName}.pdf`);
}
